// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { ClerkProvider } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { StableID, StatsigProvider } from '@statsig/react-bindings';
import posthog from 'posthog-js';
import { createRoot } from 'react-dom/client';

const isProductionEnv =
  window.location.origin === 'https://study.240tutoring.com' ||
  window.location.origin === 'https://study.240.org' ||
  window.location.origin === 'https://samtest.240tutoring.com';

Sentry.init({
  dsn: 'https://f319e6aad5717d2546bae4f876d3f0db@o4510822750683136.ingest.us.sentry.io/4510822763921408',
  environment: isProductionEnv ? 'production' : 'development',
  // Tag events with the build version so they group by release and match the
  // source maps uploaded at build time (see sentryVitePlugin in vite.config.ts).
  release: import.meta.env.VITE_BUILD_VERSION,
  enabled: true,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Sample 1% of transactions to reduce Sentry spend
  tracesSampleRate: 0.01,
  // Set tracePropagationTargets for distributed tracing
  // Only trace our own API - exclude third-party services like Clerk
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.240tutoring\.com/,
    /^https:\/\/api\.240\.org/,
  ],
  // Session Replay - 1% of sessions, 1% on error
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.01,
  // Stale-asset chunk-load errors from clients holding outdated bundle hashes
  // after a deploy — we already reload via the vite:preloadError listener below.
  ignoreErrors: [/Unable to preload CSS for /],
});

(async () => {
  try {
    const config = await fetch(`/config/${window.location.hostname}.json`).then(
      (r) =>
        r.ok ? r.json() : fetch(`/config/default.json`).then((r) => r.json()),
    );

    window.ReactAppEnv = config;
    window.ReactAppEnv.version = import.meta.env.VITE_BUILD_VERSION;

    if (config && config.posthog && config.posthog.key) {
      posthog.init(config.posthog.key, {
        api_host: config.posthog.host,
        defaults: '2026-05-30',
        autocapture: false,
        // Dead clicks default to the project-level remote config rather than
        // off, so they survived disabling autocapture — ~150k/day, more than
        // pageviews. Set explicitly so the client, not the PostHog UI, decides.
        capture_dead_clicks: false,
        session_recording: {
          sampleRate: 0.01,
        },
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
      });
    }
    // Import your Publishable Key
    const PUBLISHABLE_KEY =
      config && config.CLERK_PUBLISHABLE_KEY
        ? config.CLERK_PUBLISHABLE_KEY
        : 'pk_test_ZW5hYmxpbmctY2hpbXAtNTQuY2xlcmsuYWNjb3VudHMuZGV2JA';

    // See https://vitejs.dev/guide/build#load-error-handling
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    window.addEventListener('vite:preloadError', (event) => {
      window.location.reload();
    });

    const { App, createHistory, Router } = await import('./AppPreload');
    const history = createHistory();

    const container = document.getElementById('root');
    const root = createRoot(container);
    const STATSIG_SDK_KEY =
      'client-I5gNFmNFABgfbhBGBrbzGP1zbZGhtqet2zUXD5VvrDO';

    const isSatellite = isProductionEnv ? true : false;
    const targetDomain = isProductionEnv ? '240tutoring.com' : undefined;
    const allowedRedirectDomains = isProductionEnv
      ? [
          '240tutoring.com',
          '240.org',
          'study.240tutoring.com',
          'samtest.240tutoring.com',
        ]
      : undefined;
    const signInUrl = isProductionEnv
      ? 'https://accounts.240.org/sign-in'
      : undefined;
    const signUpUrl = isProductionEnv
      ? 'https://accounts.240.org/sign-up'
      : undefined;

    root.render(
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        isSatellite={isSatellite}
        domain={targetDomain}
        signInUrl={signInUrl}
        signUpUrl={signUpUrl}
        allowedRedirectDomains={allowedRedirectDomains}
      >
        <Router history={history}>
          <StatsigProvider
            sdkKey={STATSIG_SDK_KEY}
            user={{
              userID: undefined,
              email: undefined,
              customIDs: { stableID: StableID.get(STATSIG_SDK_KEY) },
            }}
            options={{
              environment: {
                tier: isProductionEnv ? 'production' : 'staging',
              },
            }}
          >
            <App />
          </StatsigProvider>
        </Router>
      </ClerkProvider>,
    );
  } catch (e) {
    console.error('Unable to download app environment config.');
    console.error(e);
  }
})();
